// types
import type {
  AutocompletePrediction,
  Options,
  Suggestion,
  RawSuggestion,
} from '../../locator.types';

// utils
import { GeocoderStatus } from '../../constants';
import { Log } from 'services/log';
import { isEmpty } from 'utils/is-empty';

export function buildOptions({
  coordinates,
  country,
  googleMapsRef,
  mode,
  radius,
  userInput,
}: Options) {
  if (!googleRef) return;
  const options: google.maps.places.AutocompletionRequest = {
    input: userInput,
    location: new googleMapsRef!.LatLng(coordinates.lat, coordinates.lng),
    radius,
    componentRestrictions: { country: '' },
  };

  // if we have a country prop set, add the componentRestrictions property
  // to the options object
  if (mode !== 'hotel' && country && country !== 'GISA') {
    if (country === 'CH') {
      options.componentRestrictions = {
        country: ['CH', 'LI'],
      };
    } else {
      options.componentRestrictions = {
        country,
      };
    }
  }

  if (options.componentRestrictions?.country && options.componentRestrictions.country.length > 5) {
    options.componentRestrictions.country = null;
  }
  return options;
}

enum StatusMessage {
  OK = 'The response contains a valid result.',
  ZERO_RESULTS = '',
  INVALID_REQUEST = 'This request was invalid.',
  OVER_QUERY_LIMIT = 'The application has gone over its request quota.',
  REQUEST_DENIED = 'The application is not allowed to use the PlacesService.',
  UNKNOWN_ERROR = `The PlacesService request could not be processed \n
                due to a server error.The request may succeed if you try again.`,
}

export function buildPromiseCreator(
  autocompleteService: google.maps.places.AutocompleteService,
  options: google.maps.places.AutocompletionRequest,
) {
  return (type: string) =>
    new Promise<{ payload: AutocompletePrediction[]; message: string } | Record<string, never>>(
      (resolve, reject) => {
        autocompleteService.getPlacePredictions(
          { types: [type], ...options },
          (
            suggestions: AutocompletePrediction[],
            status: google.maps.places.PlacesServiceStatus,
          ) => {
            if (status === 'OK') {
              resolve({ payload: suggestions, message: StatusMessage.OK });
            }

            if (status === 'ZERO_RESULTS') {
              resolve({});
            }

            reject(StatusMessage[status]);
          },
        );
      },
    );
}
/**
 * Normalize the suggestions and persist them in local state.
 * This triggers a re-render and displays all suggestions inside
 * the <SuggestList> component.
 * @param {Array} updatedSuggests - An array containing all google autocomplete suggestions.
 */
export const normalizeSuggests = (
  updatedSuggests?: AutocompletePrediction[],
): Suggestion[] | null => {
  // If we have suggestions, normalize them...
  if (updatedSuggests) {
    const normalizedSuggests = updatedSuggests.map((suggest: AutocompletePrediction) => ({
      mainText: suggest.structured_formatting.main_text,
      secondaryText: suggest.structured_formatting.secondary_text,
      matchedSubstrings: suggest.structured_formatting.main_text_matched_substrings[0],
      description: suggest.description,
      placeId: suggest.place_id,
    }));

    return normalizedSuggests;
  }

  return null;
};

const updateSuggests = (setSuggests, updatedSuggests?: RawSuggestion[]) => {
  // If we have suggests, normalize them...
  if (updatedSuggests) {
    const normalizedSuggests = updatedSuggests.map((suggest) => ({
      mainText: suggest.structured_formatting.main_text,
      secondaryText: suggest.structured_formatting.secondary_text,
      matchedSubstrings: suggest.structured_formatting.main_text_matched_substrings[0],
      description: suggest.description,
      placeId: suggest.place_id,
    }));
    setSuggests(normalizedSuggests);
    // ...else reset the state.
  } else {
    setSuggests(null);
  }
};

const createPromise = (autocompleteService, options, type) =>
  new Promise<{ message: string; payload: RawSuggestion[] }>((resolve, reject) => {
    autocompleteService.current.getPlacePredictions(
      { types: [type], ...options },
      (suggestions, status) => {
        if (status === 'OK') {
          const message = 'The response contains a valid result.';
          resolve({ payload: suggestions, message });
        }

        if (status === 'INVALID_REQUEST') {
          const message = 'This request was invalid.';
          reject(message);
        }
        if (status === 'OVER_QUERY_LIMIT') {
          const message = 'The application has gone over its request quota.';
          reject(message);
        }
        if (status === 'REQUEST_DENIED') {
          const message = 'The application is not allowed to use the PlacesService.';
          reject(message);
        }
        if (status === 'UNKNOWN_ERROR') {
          const message = `The PlacesService request could not be processed \n
        due to a server error.The request may succeed if you try again.`;
          reject(message);
        }
        if (status === 'ZERO_RESULTS') {
          resolve({ message: '', payload: [] });
        }
      },
    );
  });

export async function searchSuggests({
  userInput,
  setSuggests,
  minLength,
  googleMapsRef,
  coordinates,
  country,
  mode,
  radius,
  pinCountries,
  autocompleteService,
}) {
  try {
    // if we dont have a userInput, update the suggests
    if (isEmpty(userInput)) {
      updateSuggests(setSuggests);
      return;
    }
    // if the userinput length is less than the required stringlength, return
    if (userInput.length < minLength) {
      return;
    }

    // build the getPlacePredictions options object
    const options: google.maps.places.AutocompletionRequest = {
      input: userInput,
      location: new googleMapsRef.LatLng(coordinates.lat, coordinates.lng),
      radius,
    };

    // if we have a country prop set, add the componentRestrictions property
    // to the options object
    if (mode !== 'hotel' && country && country !== 'GISA') {
      if (country === 'CH') {
        options.componentRestrictions = {
          country: ['CH', 'LI'],
        };
      } else {
        options.componentRestrictions = {
          country,
        };
      }
    } else if (mode === 'hotel') {
      options.componentRestrictions = {
        country: pinCountries,
      };
    }
    if (
      options.componentRestrictions?.country &&
      options.componentRestrictions.country.length > 5
    ) {
      options.componentRestrictions.country = null;
    }

    const placeTypes = ['(regions)', 'address'];
    const promises = placeTypes.map((type) => createPromise(autocompleteService, options, type));

    // Await the getPlacePredictions by wrapping the default google request method with
    // a new promise wrapper.
    const predictions = await Promise.all(promises);
    const allResults: RawSuggestion[] = [];
    predictions.forEach((result) => result.payload && allResults.push(...result.payload));

    updateSuggests(setSuggests, allResults);
  } catch (error) {
    Log.error('Failed to get suggestions', error);
  }
}

function geocodeSuggest({
  suggest,
  mode,
  country,
  pinCountries,
  geocoderService,
  setCurrentCoords,
  setGeocoded,
  maxDistance,
  setMaxDistance,
}) {
  const suggestOption = { ...suggest };

  let options: google.maps.GeocoderRequest = {};
  if (suggest?.placeId) {
    options = { placeId: suggest.placeId };
  } else if (suggest) {
    options = {
      address: suggest.label,
    };

    if (mode !== 'hotel' && country) {
      options.componentRestrictions = {
        country,
      };
    } else if (mode === 'hotel' && pinCountries?.[0]) {
      options.componentRestrictions = {
        country: pinCountries[0],
      };
    }
  }
  if (
    options.componentRestrictions?.country &&
    options.componentRestrictions?.country?.length > 5
  ) {
    delete options.componentRestrictions.country;
  }

  geocoderService?.geocode(options, (results, status) => {
    if (status === GeocoderStatus.OK && results && results.length > 0) {
      const details = results[0];
      const { location } = details.geometry;
      suggestOption.location = {
        lat: location.lat(),
        lng: location.lng(),
      };

      setCurrentCoords({
        lat: location.lat(),
        lng: location.lng(),
      });
      setGeocoded(true);

      // don't reset distance if it is already selected
      if (maxDistance === 'none') {
        setMaxDistance(50000);
      }
    }
  });
}

export function selectSuggest({
  suggest,
  userInput,
  onInputBlur,
  setAutoInput,
  suggests,
  mode,
  country,
  pinCountries,
  geocoderService,
  setCurrentCoords,
  setGeocoded,
  maxDistance,
  setMaxDistance,
  track,
  locatorEvent,
  setUserInput,
}) {
  // Cancel if we don't have a user input
  if (isEmpty(userInput)) {
    return;
  }
  onInputBlur();
  setAutoInput(true);

  // When we dont have a suggest, use the first google autosuggest
  // (e.g. when the user hits enter or uses the search-button)
  if (isEmpty(suggest) && !isEmpty(suggests)) {
    geocodeSuggest({
      suggest: suggests?.[0],
      mode,
      country,
      pinCountries,
      geocoderService,
      setCurrentCoords,
      setGeocoded,
      maxDistance,
      setMaxDistance,
    });
    return;
  }

  if (!isEmpty(suggest)) {
    setUserInput(suggest.description);
    track.trackEvent(locatorEvent('Locator-Search', suggest.description));
  } else {
    setUserInput('');
  }

  geocodeSuggest({
    suggest,
    mode,
    country,
    pinCountries,
    geocoderService,
    setCurrentCoords,
    setGeocoded,
    maxDistance,
    setMaxDistance,
  });
}
